import type { ObjectId } from 'mongodb';

import type { ApprovedVendorAllocation, CalculatorType, FeocDataItem, VendorEquipmentTypes } from './AppSettings';
import type { InverterType } from './DomesticContentTypes';
import type { Meta } from './Entities';
import type { IsoDateString, IsoDateTimeString } from './Helpers';
import type { ProgramType } from './ProgramTypes';

export const avlTimelineStart: IsoDateString = '2020-01-01';

/** Append-only: `endDate` is server-derived (never sent by the client); `setAt`/`setBy` never change. */
export interface AvlAllocationDetail {
  id: string;
  startDate: IsoDateString;
  endDate: IsoDateString | null;
  allocationStatus: ApprovedVendorAllocation;
  setAt: IsoDateTimeString;
  setBy: Meta['modifiedBy'];
  /** Marks a row the timeline ignores, set only by support corrections; no API or UI sets it. */
  archived?: boolean;
  /** Shares a bulk-edit request's `bulkEditId` when this row was appended via the bulk-update route. */
  correlationId?: string;
  /** Resolved display name for `setBy`, added by `enrichWithUserNames`; never stored. */
  setByName?: string;
}

/**
 * A single change-history entry appended on every update of an AVL item.
 *
 * Declared independently of `EntityHistoryEntry` even though the shapes currently match: this one
 * is part of the AVL read contract, so a change to the shared entity history entry must not
 * silently reshape it.
 */
export interface AvlItemHistoryEntry {
  changedBy: string;
  changedAt: Date;
  changes: Record<string, { from: unknown; to: unknown }>;
  note?: string;
  /** Shared across every history entry a single bulk-update request writes, so they can be correlated. */
  bulkEditId?: string;
}

/**
 * A single alias on an AVL item, tracking which system the alias originates from.
 * `source` may be an empty string ('') for unattributed aliases.
 */
export interface ApprovedVendorAlias {
  source: string;
  value: string;
}

/**
 * One equipment item in the first-class `approvedVendorList` collection.
 *
 * The optional spec fields below mirror `ApprovedVendorSettingValue` (AppSettings.ts)
 * verbatim so the read-path assembler can map an item 1:1 back to the existing
 * `ApprovedVendorSettingValue` shape without contract drift.
 */
export interface ApprovedVendorItemDocument {
  id: ObjectId;
  /** One equipment type per item. */
  equipmentType: VendorEquipmentTypes;
  manufacturer: string;
  modelNumber: string;
  name: string;
  /** stableEquipmentKey(type, item) — migration-time merge identity (not used at read time). */
  catalogKey: string;
  /**
   * The catalogKey captured at migration (or at admin creation), immutable; kept as a
   * troubleshooting breadcrumb because `catalogKey` can drift when an initially-empty identity
   * field (e.g. sku/category) is later filled in.
   */
  legacyCatalogKey?: string;
  programTypes: ProgramType[];
  /**
   * INVARIANT: unique per equipmentType, case-insensitive on value (enforced by
   * `assertAliasesUniqueForType` and the `equipmentType`+`aliases.value` unique index).
   *
   * `omnidianAlias` (below) must NEVER be written in here as a `{ source: 'omnidian', ... }`
   * entry — Omnidian's model naming is coarser than our catalog, so several storage model
   * families legitimately share one `omnidianAlias` value across multiple distinct items here.
   * Mirroring it into this array breaks the uniqueness invariant on real data. Blocked at the API
   * boundary by `assertNoOmnidianAliasSource`; see `omnidianAlias`'s doc comment below.
   */
  aliases: ApprovedVendorAlias[];
  /** Old per-programType ids retained for reference compatibility. */
  legacyIds: string[];
  allocation: ApprovedVendorAllocation;
  /** Optional until the migration seeding this from `allocation` has run everywhere. */
  allocationDetails?: AvlAllocationDetail[];
  needsApproval: boolean;

  // Remaining optional ApprovedVendorSettingValue fields (verbatim, for 1:1 assembler mapping):
  calculatorType?: CalculatorType;
  sku?: string;
  description?: string;
  category?: string;
  domesticContentSupported?: boolean;
  inverterType?: InverterType;
  bessDomesticContentPercentage?: number;
  bessFeocPercentage?: number;
  domesticContentPercentageMlpe?: number;
  domesticContentPercentageString?: number;
  feocPercentageMlpe?: number;
  feocPercentageString?: number;
  maxDCkW?: number;
  maxACkW?: number;
  gridPairedACkW?: number | null;
  gridPairedPeakACkW?: number | null;
  usableCapacityKWh?: number | null;
  maxPrice?: number;
  requiresExtendedWarranty?: boolean;
  extendedWarrantyCost?: number;
  feocData?: FeocDataItem[];
  size?: {
    value: number;
    unit: 'kW' | 'kWh' | 'watts';
  };
  arbitrage?: boolean;
  backup?: boolean;
  /**
   * Independent scalar field — deliberately kept separate from `aliases[]`. A one-time migration
   * (FIN-4809's `avlBackfill`) once mirrored this into `aliases[]`; that pattern must not be
   * repeated (see the note on `aliases` above). Alias-based lookup (`resolveAvlItemId`) never
   * reads this field, only `aliases[]`.
   */
  omnidianAlias?: string;

  history: AvlItemHistoryEntry[];
  meta: Meta;
}

/**
 * Per-item identity fields that determine `catalogKey` uniqueness (Tier 2). Not sensible to
 * bulk-set across a selection — each item's identity is its own. Mirrors the single-item PUT
 * route's `assertTier2Immutable` gate.
 */
export const AVL_TIER2_IDENTITY_FIELDS = [
  'equipmentType',
  'manufacturer',
  'modelNumber',
  'sku',
  'category',
] as const;

// Read-only panel-series metadata, computed once at series generation; never user-editable.
export const AVL_SERIES_INFO_FIELDS = ['panelWattageRange', 'panelWattageIncrement', 'panelSeriesTemplate'] as const;

// allocation is derived from allocationDetails; bulk edit only appends via addAllocationDetail.
export const AVL_ALLOCATION_FIELDS = ['allocation', 'allocationDetails'] as const;

/** Aliases must be unique per item, and a rename auto-captures the previous name as an alias. */
export const AVL_BULK_EDIT_NON_SPEC_EXCLUDED_FIELDS = ['name', 'aliases'] as const;

/**
 * Every field excluded from bulk-edit's eligible-field computation, and rejected if present in the
 * generic `changes` object of a bulk-update request.
 */
export type AvlBulkEditExcludedField =
  | (typeof AVL_TIER2_IDENTITY_FIELDS)[number]
  | (typeof AVL_SERIES_INFO_FIELDS)[number]
  | (typeof AVL_ALLOCATION_FIELDS)[number]
  | (typeof AVL_BULK_EDIT_NON_SPEC_EXCLUDED_FIELDS)[number];

export const AVL_BULK_EDIT_EXCLUDED_FIELDS: readonly AvlBulkEditExcludedField[] = [
  ...AVL_TIER2_IDENTITY_FIELDS,
  ...AVL_SERIES_INFO_FIELDS,
  ...AVL_ALLOCATION_FIELDS,
  ...AVL_BULK_EDIT_NON_SPEC_EXCLUDED_FIELDS,
];

/**
 * Request body for the bulk-update endpoint. `changes` may be empty (an allocation-detail-only
 * request), and `newAllocationDetail` is independently optional (a field-changes-only request) —
 * a request must supply at least one of the two, enforced by the route, not this type.
 */
export interface BulkUpdateApprovedVendorItemsRequest {
  ids: string[];
  changes?: Partial<ApprovedVendorItemDocument>;
  newAllocationDetail?: {
    startDate: IsoDateString;
    allocationStatus: ApprovedVendorAllocation;
  };
}

/**
 * Response for the bulk-update endpoint: partial per-item success/failure for ordinary field
 * changes. An allocation-detail conflict rejects the whole request instead of appearing here.
 */
export interface BulkUpdateApprovedVendorItemsResponse {
  updated: string[];
  failed: { id: string; error: string }[];
}
